import { useCallback } from 'react';
import { Misc } from '@/constants';
import { ProcessItem } from '@/components/common-components/advanced-mode-process-loader/AdvancedModeProcessLoaderComponent';
import { CommonService } from '@/services';
import commonService from '@/services/common.service.ts';

interface AdvancedModeOptions {
    projectId: string;
    message: string;
    previewUrl: string;
    sessionId?: string;
    assets?: any[]; // Array of assets/files to send
    image?: string; // Base64 encoded image
    onMessage: (message: any, isStreaming?: boolean) => void;
    onError: (errorMessage?: string) => void;
    onComplete: () => void;
    onProcessItem?: (item: ProcessItem) => void;
    onContainerWakeStart?: () => void;
    onContainerWakeSuccess?: (newUrl: string) => void;
    waitForPreviewUrl?: () => Promise<string>; // Function to wait for preview URL from socket
}

// Check if the container is active by making a health check request
const checkContainerStatus = async (previewUrl: string): Promise<boolean> => {
    if (!previewUrl) return false;

    try {
        const url = new URL(previewUrl);
        // Replace port 3000 with 7000 to check the execute endpoint
        const newHost = url.host.replace('3000', '7000');
        const healthCheckUrl = `${url.protocol}//${newHost}/health`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

        const response = await fetch(healthCheckUrl, {
            method: 'GET',
            signal: controller.signal
        });

        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        console.log('Container status check failed:', error);
        return false;
    }
};

export const useAdvancedMode = () => {
    const executeAdvancedMode = useCallback(async (options: AdvancedModeOptions) => {
        const {
            projectId,
            message,
            previewUrl,
            sessionId: providedSessionId,
            assets,
            image,
            onMessage,
            onError,
            onComplete,
            onProcessItem,
            onContainerWakeStart,
            onContainerWakeSuccess,
            waitForPreviewUrl
        } = options;

        // Configuration for retries
        const MAX_RETRIES = 5;
        let retryCount = 0;
        let currentSessionId = providedSessionId;

        // Track state across retries
        let isCompleted = false;
        let isAborted = false;
        let abortController: AbortController | null = null;
        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        let activityChecker: NodeJS.Timeout | null = null;

        // Hoisted state for persistence across retries and cleanup
        let finalResult = '';
        let migrationId: string | null = null;
        let sequenceCounter = 0;
        let turnCounter = 0;
        let forceRestart = false; // Flag to force restart logic

        // Helper to delay for backoff
        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        while (retryCount <= MAX_RETRIES && !isCompleted && !isAborted) {
            try {
                // If this is a retry, notify user
                if (retryCount > 0) {
                    console.log(`Attempting to reconnect (Attempt ${retryCount}/${MAX_RETRIES})...`);
                    if (onProcessItem) {
                        onProcessItem({
                            id: `reconnect-${Date.now()}`,
                            type: 'message',
                            content: `Connection lost. Reconnecting (Attempt ${retryCount}/${MAX_RETRIES})...`,
                            status: 'streaming',
                            timestamp: Date.now()
                        });
                    }
                }

                const jwtToken = localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);
                if (!jwtToken) {
                    onError('Authentication token not found');
                    return;
                }

                let activePreviewUrl = previewUrl;

                // On first attempt, or if we need to ensure container is ready
                // (For retries, we assume potential container issues might be transient or related to the disconnect,
                // but checking status again is safer if the drop was due to container sleep)

                // ... [Container check logic - kept mostly same but streamlined] ...
                // Check if we have a valid preview URL and if the container is active
                if (!previewUrl) {
                    // No preview URL - need to wake up container
                    if (retryCount === 0) { // Only do full wake sequence on first try ideally, or logic needs to be robust
                        if (onProcessItem) {
                            onProcessItem({
                                id: `container-wake-${Date.now()}`,
                                type: 'message',
                                content: 'Starting container...',
                                status: 'streaming',
                                timestamp: Date.now()
                            });
                        }
                        onContainerWakeStart?.();

                        // Trigger session activation using revertToASession API
                        commonService._project_service.revertToASession(projectId, {});

                        // Wait for the preview URL from socket event
                        if (waitForPreviewUrl) {
                            try {
                                activePreviewUrl = await waitForPreviewUrl();
                                onContainerWakeSuccess?.(activePreviewUrl);

                                if (onProcessItem) {
                                    onProcessItem({
                                        id: `container-wake-${Date.now()}`,
                                        type: 'message',
                                        content: 'Container ready!',
                                        status: 'completed',
                                        timestamp: Date.now()
                                    });
                                }
                            } catch {
                                onError('Failed to start container. Please try again.');
                                return; // Fatal error
                            }
                        } else {
                            onError('Preview URL not available');
                            return; // Fatal error
                        }
                    }
                } else {
                    // We have a preview URL - check if container is active
                    // We prioritize this check to ensure we don't hit a dead container loop
                    // If forceRestart is true, we skip the check and assume we need to wake/restart
                    // This logic is handled by setting isActive = false if forceRestart is true
                    let isActive = false;
                    if (!forceRestart) {
                        isActive = await checkContainerStatus(previewUrl);
                    } else {
                        console.log('Force restart active - skipping health check and forcing wake sequence');
                        forceRestart = false; // Reset flag
                    }

                    if (!isActive) {
                        // Container is paused - wake it up
                        if (onProcessItem) {
                            onProcessItem({
                                id: `container-wake-${Date.now()}`,
                                type: 'message',
                                content: retryCount === 0 ? 'Waking up container...' : 'Container sleeping, waking up...',
                                status: 'streaming',
                                timestamp: Date.now()
                            });
                        }
                        onContainerWakeStart?.();

                        // Trigger session activation using revertToASession API
                        commonService._project_service.revertToASession(projectId, {});

                        // Wait for the preview URL from socket event
                        if (waitForPreviewUrl) {
                            try {
                                activePreviewUrl = await waitForPreviewUrl();
                                onContainerWakeSuccess?.(activePreviewUrl);

                                if (onProcessItem) {
                                    onProcessItem({
                                        id: `container-wake-${Date.now()}`,
                                        type: 'message',
                                        content: 'Container ready!',
                                        status: 'completed',
                                        timestamp: Date.now()
                                    });
                                }
                            } catch {
                                throw new Error('Failed to wake up container during reconnect.');
                            }
                        } else {
                            // No waitForPreviewUrl function - try to proceed anyway but log warning
                            console.log('No waitForPreviewUrl provided, proceeding with existing URL');
                        }
                    }
                }

                // Extract preview server URL from preview URL
                let previewServerUrl: string;
                try {
                    const url = new URL(activePreviewUrl);
                    const newHost = url.host.replace('3000', '7000');
                    previewServerUrl = `${url.protocol}//${newHost}/execute`;
                } catch (urlError) {
                    console.error('Error parsing preview URL:', urlError);
                    onError('Invalid preview URL');
                    return; // Fatal
                }

                // Prepare request body
                const requestBody: any = {
                    message: message,
                    cwd: "/app/apps/current_session",
                    projectId: projectId,
                };

                // Add assets if available
                if (assets && assets.length > 0) {
                    requestBody.assets = assets;
                }

                // Add image if available
                if (image) {
                    requestBody.image = image;
                }

                // IMPORTANT: Use currentSessionId if available to resume session
                if (currentSessionId) {
                    requestBody.sessionId = currentSessionId;
                }

                // Create abort controller for request timeout
                abortController = new AbortController();
                // 25 minutes timeout (1500000ms) matches code, corrected comment
                const timeoutId = setTimeout(() => {
                    if (abortController) {
                        console.warn('Request timeout - aborting');
                        abortController.abort();
                    }
                }, 1500000);

                // Make POST request
                const response = await fetch(previewServerUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${jwtToken}`
                    },
                    body: JSON.stringify(requestBody),
                    signal: abortController.signal,
                });

                // Clear the timeout once we get a response
                clearTimeout(timeoutId);

                if (!response.ok) {
                    // Check for specific "no IP address found" error
                    try {
                        const errorResponse = await response.clone().json();
                        const errorMsg = errorResponse?.error || errorResponse?.message || '';

                        if (errorMsg.includes('no IP address found') || errorMsg.includes('Sandbox started')) {
                            console.log('Caught "no IP address found" error - forcing container restart');
                            forceRestart = true;
                            throw new Error('Container IP missing - forcing restart');
                        }
                    } catch (e) {
                        // Ignore clone/json parse error, fall through to normal handling
                    }

                    // If error is 5xx or network related, throw to trigger retry
                    // If 4xx (client error), likely fatal unless 429
                    if (response.status >= 500 || response.status === 429) {
                        throw new Error(`Server error: ${response.status}`);
                    }

                    try {
                        const errorData = await response.json();
                        onError(errorData?.error || errorData?.message || response.statusText || 'Error reading response stream');
                    } catch {
                        onError(response?.statusText || 'Error reading response stream');
                    }
                    return; // Fatal client error
                }

                // Handle Server-Sent Events (SSE)
                reader = response.body ? response.body.getReader() : null;
                if (!reader) {
                    throw new Error('Failed to read response stream');
                }

                // Reset retry count on successful connection (or at least successful stream start)
                // However, we only reset if we get meaningful data, maybe? 
                // For now, let's keep retry logic simple. If we break out of loop, we are done.

                const decoder = new TextDecoder();
                let buffer = '';
                let pendingEventType: string | null = null;
                // finalResult, sequenceCounter, turnCounter, migrationId are hoisted
                const toolNamesMap = new Map<string, string>();
                let lastActivityTime = Date.now();
                const ACTIVITY_TIMEOUT = 300000; // 5 minutes without any data

                // Activity checker logic
                if (activityChecker) clearInterval(activityChecker);
                activityChecker = setInterval(() => {
                    const timeSinceLastActivity = Date.now() - lastActivityTime;
                    if (timeSinceLastActivity > ACTIVITY_TIMEOUT) {
                        console.warn('No activity detected for 5 minutes, connection may be stale');
                        // Use throwing to trigger retry loop
                        if (abortController) abortController.abort(); // Cancel current request
                    }
                }, 10000); // Check every 10 seconds

                try {
                    while (true) {
                        const { done, value } = await reader.read();

                        if (done) {
                            console.log('Stream completed successfully');
                            isCompleted = true; // Mark as success to exit retry loop
                            break;
                        }

                        // Update last activity time
                        lastActivityTime = Date.now();
                        // Also reset retry count if we are receiving data successfully for a while? 
                        // Maybe reset if we survive 10 seconds? For now, leave simple.

                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;

                        // Process complete lines
                        let newlineIndex;
                        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
                            const line = buffer.slice(0, newlineIndex);
                            buffer = buffer.slice(newlineIndex + 1);
                            const trimmedLine = line.trim();

                            // Skip keepalive comments
                            if (trimmedLine.startsWith(':')) {
                                // console.log('Received keepalive');
                                continue;
                            }

                            // Parse SSE format
                            if (trimmedLine.startsWith('event:')) {
                                pendingEventType = trimmedLine.replace(/^event:\s*/, '').trim();
                            } else if (trimmedLine.startsWith('data:')) {
                                const jsonStr = trimmedLine.replace(/^data:\s*/, '');
                                if (!jsonStr) continue;

                                try {
                                    const data = JSON.parse(jsonStr);
                                    const eventType = pendingEventType || 'message';
                                    pendingEventType = null;

                                    if (!onProcessItem) continue;

                                    // Handle different event types
                                    switch (eventType) {
                                        case 'session':
                                            // Capture session ID for potential reconnects
                                            currentSessionId = data?.sessionId || data?.session_id || null;
                                            localStorage.setItem(Misc.LOCAL_STORAGE_CLAUDE_SESSION_ID, currentSessionId || '');

                                            // Pass through to original handler
                                            sequenceCounter++;
                                            onProcessItem({
                                                id: `session-${currentSessionId}`,
                                                type: 'message',
                                                content: `Session initialized: ${currentSessionId?.substring(0, 8)}...`,
                                                status: 'completed',
                                                timestamp: Date.now() + sequenceCounter
                                            });
                                            break;

                                        // ... [Other cases remain largely identical] ...
                                        case 'ping':
                                            break;
                                        case 'history_fetch':
                                            break;
                                        case 'credentials_fetch':
                                            console.log('Credentials fetch:', data.status);
                                            break;
                                        case 'start':
                                            sequenceCounter++;
                                            onProcessItem({
                                                id: `start-${Date.now()}`,
                                                type: 'message',
                                                content: 'Starting advanced mode...',
                                                status: 'completed',
                                                timestamp: Date.now() + sequenceCounter
                                            });
                                            commonService._communications.setAdvancedModePreviewLoadingSubject.next(true);
                                            break;
                                        case 'message': {
                                            const messageData = data;
                                            const fullMessage = messageData?.fullMessage;

                                            if (messageData?.type === 'system' && messageData?.subtype === 'init') {
                                                sequenceCounter++;
                                                const tools = fullMessage?.tools || [];
                                                onProcessItem({
                                                    id: `init-${currentSessionId || Date.now()}`,
                                                    type: 'message',
                                                    content: `Initialized with ${tools.length} tools available`,
                                                    status: 'completed',
                                                    timestamp: Date.now() + sequenceCounter
                                                });
                                            } else if (messageData?.type === 'assistant') {
                                                turnCounter++;
                                                const content = fullMessage?.message?.content || [];

                                                if (Array.isArray(content)) {
                                                    content.forEach((item: any) => {
                                                        if (item.type === 'tool_use') {
                                                            sequenceCounter++;
                                                            const toolName = item.name || 'Tool';
                                                            const input = item.input || {};

                                                            toolNamesMap.set(item.id, toolName);

                                                            let description = `Executing ${toolName}`;
                                                            if (toolName === 'Edit' && input.file_path) {
                                                                description = `Editing ${input.file_path}`;
                                                            } else if (toolName === 'Write' && input.file_path) {
                                                                description = `Writing ${input.file_path}`;
                                                            } else if (toolName === 'Read' && input.file_path) {
                                                                description = `Reading ${input.file_path}`;
                                                            } else if (toolName === 'Bash' && input.command) {
                                                                description = `Running: ${input.command}`;
                                                            }

                                                            onProcessItem({
                                                                id: `tool-${item.id}`,
                                                                type: 'tool-call',
                                                                toolName: toolName,
                                                                content: description,
                                                                status: 'streaming',
                                                                timestamp: Date.now() + sequenceCounter
                                                            });
                                                        } else if (item.type === 'text' && item.text) {
                                                            sequenceCounter++;
                                                            onProcessItem({
                                                                id: `assistant-text-${turnCounter}-${sequenceCounter}`,
                                                                type: 'message',
                                                                content: item.text,
                                                                status: 'streaming',
                                                                timestamp: Date.now() + sequenceCounter
                                                            });
                                                        }
                                                    });
                                                }
                                            } else if (messageData?.type === 'user') {
                                                const content = fullMessage?.message?.content || [];
                                                if (Array.isArray(content)) {
                                                    content.forEach((item: any) => {
                                                        if (item.type === 'tool_result' && item.tool_use_id) {
                                                            const originalToolName = toolNamesMap.get(item.tool_use_id) || 'Tool';
                                                            const toolResult = typeof item.content === 'string'
                                                                ? item.content
                                                                : JSON.stringify(item.content, null, 2);

                                                            onProcessItem({
                                                                id: `tool-${item.tool_use_id}`,
                                                                type: 'tool-call',
                                                                toolName: originalToolName,
                                                                content: toolResult.substring(0, 500),
                                                                status: 'completed',
                                                                timestamp: Date.now()
                                                            });
                                                        }
                                                    });
                                                }
                                            } else if (messageData?.type === 'result') {
                                                finalResult = messageData?.result || '';
                                                sequenceCounter++;
                                                onProcessItem({
                                                    id: `result-text-${currentSessionId || Date.now()}`,
                                                    type: 'message',
                                                    content: finalResult,
                                                    status: 'completed',
                                                    timestamp: Date.now() + sequenceCounter
                                                });
                                            }
                                            break;
                                        }

                                        case 'session_save_start':
                                            sequenceCounter++;
                                            onProcessItem({
                                                id: `session-save-start-${Date.now()}`,
                                                type: 'message',
                                                content: data?.action || 'Saving session...',
                                                status: 'streaming',
                                                timestamp: Date.now() + sequenceCounter
                                            });
                                            break;
                                        case 'session_save_progress':
                                            onProcessItem({
                                                id: `session-save-start-${Date.now() - 1}`,
                                                type: 'message',
                                                content: data?.message || `Converted ${data?.fileCount || 0} files...`,
                                                status: 'streaming',
                                                timestamp: Date.now() + sequenceCounter
                                            });
                                            break;
                                        case 'session_save_end':
                                            onProcessItem({
                                                id: `session-save-start-${Date.now() - 1}`,
                                                type: 'message',
                                                content: data?.success ? 'Session saved successfully' : 'Session save failed',
                                                status: 'completed',
                                                timestamp: Date.now() + sequenceCounter
                                            });
                                            break;
                                        case 'chat_save_start':
                                            sequenceCounter++;
                                            onProcessItem({
                                                id: `chat-save-${Date.now()}`,
                                                type: 'message',
                                                content: 'Saving chat conversation...',
                                                status: 'streaming',
                                                timestamp: Date.now() + sequenceCounter
                                            });
                                            break;
                                        case 'chat_save_end':
                                            onProcessItem({
                                                id: `chat-save-${Date.now() - 1}`,
                                                type: 'message',
                                                content: data?.success ? 'Chat saved successfully' : 'Chat save failed',
                                                status: 'completed',
                                                timestamp: Date.now() + sequenceCounter
                                            });
                                            break;
                                        case 'warning':
                                            console.warn('Server warning:', data);
                                            break;
                                        case 'complete':
                                            if (data?.result) {
                                                const resultData = data.result;
                                                const costUSD = resultData?.total_cost_usd || 0;
                                                const numTurns = resultData?.num_turns || 0;

                                                sequenceCounter++;
                                                onProcessItem({
                                                    id: `complete-${currentSessionId || Date.now()}`,
                                                    type: 'message',
                                                    content: `Completed in ${numTurns} turns (Cost: $${costUSD.toFixed(4)})`,
                                                    status: 'completed',
                                                    total_cost_usd: costUSD,
                                                    model_usage: resultData?.usage,
                                                    timestamp: Date.now() + sequenceCounter,
                                                    is_completed: true
                                                });

                                                if (!finalResult) {
                                                    finalResult = resultData?.result || '';
                                                }
                                                // IMPORTANT: Flag as completed so we don't reconnect
                                                isCompleted = true;
                                                // Abort the connection immediately
                                                if (abortController) {
                                                    abortController.abort();
                                                }
                                            }
                                            break;
                                        case 'migration_file_generated':
                                            console.log('migration_file_generated', data);
                                            migrationId = data?.migration_id || null;
                                            break;
                                        case 'error':
                                            // Handle specific errors that should NOT retry
                                            // For now, treat unexpected server errors as potentially retriable if not fatal
                                            // But if server explicitly sends 'error' event, it usually means business logic failure, not net failure
                                            onError(data?.message || 'Failed to execute advanced mode');
                                            isCompleted = true; // Stop retries
                                            return; // Break out
                                        default:
                                            console.log('Unknown event type:', eventType, data);
                                    }
                                } catch (parseError) {
                                    console.error('Error parsing SSE data:', parseError, jsonStr);
                                }
                            }

                            // If completed, break the line processing loop
                            if (isCompleted) break;
                        }

                        // If completed, break the reader loop
                        if (isCompleted) break;
                    } // End while(reader)

                    // If we exit reader loop cleanly (done=true), isCompleted is already set to true.
                    // If we exit due to error, we catch below:

                } catch (streamError: unknown) {
                    // If we completed successfully, ignore abort errors
                    if (isCompleted) {
                        console.log('Stream completed and connection aborted intentionally');
                    } else {
                        console.error('Error reading SSE stream:', streamError);

                        // If manually aborted by user or timeout, check AbortSignal
                        if (abortController?.signal.aborted) {
                            // If it was our activity timeout logic
                            if (Date.now() - lastActivityTime > ACTIVITY_TIMEOUT) {
                                throw new Error('Activity timeout');
                            }
                        }

                        throw streamError; // Re-throw to trigger retry loop
                    }
                } finally {
                    if (reader) {
                        try {
                            reader.releaseLock();
                        } catch (e) {
                            console.warn('Error releasing reader lock:', e);
                        }
                    }
                    if (activityChecker) {
                        clearInterval(activityChecker);
                        activityChecker = null;
                    }
                }

            } catch (error: any) {
                // Catch-all for the retry loop iteration
                console.warn(`Attempt ${retryCount + 1} failed:`, error);

                // Determine if we should retry
                if (retryCount < MAX_RETRIES) {
                    const backoffTime = 1000 * Math.pow(2, retryCount); // 1s, 2s, 4s, 8s, 16s
                    retryCount++;

                    // Don't sleep if we are aborted
                    if (!isAborted) {
                        await delay(backoffTime);
                    }
                } else {
                    console.error('Max retries reached. Giving up.');
                    // Final error handling
                    if (error.name === 'AbortError') {
                        onError('Request timed out. Please try again.');
                    } else if (error.message?.includes('Failed to fetch')) {
                        onError('Network error. Max retries reached.');
                    } else {
                        onError(error?.message || 'Failed to execute advanced mode after multiple attempts');
                    }
                    isAborted = true; // Stop loop
                }
            }
        } // End retry loop

        // Final Cleanup & Success callbacks
        if (isCompleted) {
            commonService._communications.setProjectUrlSubject.next(previewUrl);

            // Send the final result message if we have one
            if (finalResult && onMessage) {
                onMessage({
                    id: Date.now() + Math.random(),
                    role: 'agent',
                    message: finalResult,
                    extra: {
                        session_id: currentSessionId,
                        is_streaming: false,
                        is_completed: true,
                        migration_id: migrationId
                    }
                }, false);
            }

            CommonService._communications.fetchProjectCode.next(true);
            onComplete();
        }

    }, []);

    return {
        executeAdvancedMode
    };
};